/**
 * Microsoft sign-in service
 *
 * Owns the MSAL public client instance and exposes the two operations the app
 * needs: acquire an ID token interactively, and clear the Microsoft session on
 * logout. Everything downstream of the ID token is the backend's job.
 */

import {
  PublicClientApplication,
  BrowserAuthError,
  InteractionRequiredAuthError,
  AuthError
} from '@azure/msal-browser';
import { msalConfig, loginRequest, isMicrosoftAuthEnabled } from '../config/msalConfig';

/** Raised when the user closes the popup or otherwise abandons sign-in. */
export class MicrosoftSignInCancelled extends Error {
  constructor() {
    super('Microsoft sign-in was cancelled.');
  }
}

let instance: PublicClientApplication | null = null;
let initialization: Promise<PublicClientApplication> | null = null;
let inFlightSignIn: Promise<string> | null = null;

/**
 * MSAL's own "an interaction is running" marker, in sessionStorage.
 *
 * MSAL sets this when a popup opens and clears it when the popup settles. If
 * the parent page is reloaded or navigated while a popup is still open that
 * cleanup never runs, and because sessionStorage survives a reload the marker
 * is left behind - every later `loginPopup` then fails with
 * `interaction_in_progress` until the tab is closed.
 *
 * The key is unprefixed and not client-scoped (msal-browser writes
 * `msal.interaction.status` with the client id inside the value), and MSAL
 * exposes no public API to reset it - `allowOverride` is internal - so we
 * clear the entry directly.
 */
const INTERACTION_STATUS_KEY = 'msal.interaction.status';

const clearStaleInteraction = (): void => {
  try {
    window.sessionStorage.removeItem(INTERACTION_STATUS_KEY);
  } catch (error) {
    // Storage can throw when cookies/site data are blocked. Nothing we can do,
    // and nothing worth failing sign-in over.
    console.warn('[MicrosoftAuth] Could not clear stale interaction state:', error);
  }
};

const isInteractionInProgress = (error: unknown): boolean =>
  error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress';

/**
 * Lazily create and initialise the MSAL instance.
 *
 * MSAL v5 requires an explicit `initialize()` before any other call. The
 * promise is memoised so concurrent callers - and React StrictMode's double
 * effect invocation in development - share a single initialisation.
 */
const getMsalInstance = async (): Promise<PublicClientApplication> => {
  if (!isMicrosoftAuthEnabled) {
    throw new Error(
      'Microsoft sign-in is not configured. Set REACT_APP_AZURE_TENANT_ID and REACT_APP_AZURE_CLIENT_ID.'
    );
  }

  if (instance) return instance;

  if (!initialization) {
    initialization = (async () => {
      const created = new PublicClientApplication(msalConfig);
      await created.initialize();

      // A popup interaction cannot outlive the page that opened it, so any
      // marker still present on load belongs to an abandoned attempt.
      clearStaleInteraction();

      instance = created;
      return created;
    })();
  }

  return initialization;
};

/**
 * Sign in with Microsoft and return the resulting ID token.
 *
 * Uses a popup so the SPA is not torn down and rebuilt mid-flow. Attempts a
 * silent acquisition first when an account is already cached, so a user who
 * signs out of the app but not out of Microsoft is not prompted again.
 *
 * @returns The raw ID token JWT, to be posted to `POST /api/auth/microsoft`
 * @throws MicrosoftSignInCancelled if the user dismissed the popup
 */
export const signInWithMicrosoft = async (): Promise<string> => {
  // A second click while a popup is already open would trip MSAL's own
  // interaction guard and strand the first attempt. Share the pending one.
  if (inFlightSignIn) return inFlightSignIn;

  inFlightSignIn = attemptSignIn().finally(() => {
    inFlightSignIn = null;
  });

  return inFlightSignIn;
};

/**
 * Open the sign-in popup, recovering once from a stranded interaction marker.
 *
 * `interaction_in_progress` is thrown before the popup is even opened, so
 * clearing the marker and retrying is safe: there is no real popup to collide
 * with, and the single-flight guard above rules out one of our own.
 */
const loginPopupWithRecovery = async (
  msal: PublicClientApplication,
  isRetry = false
): Promise<string> => {
  try {
    const result = await msal.loginPopup(loginRequest);

    if (!result.idToken) {
      throw new Error('Microsoft did not return an ID token.');
    }

    return result.idToken;
  } catch (error) {
    if (!isRetry && isInteractionInProgress(error)) {
      console.warn('[MicrosoftAuth] Clearing stranded interaction state and retrying.');
      clearStaleInteraction();
      return loginPopupWithRecovery(msal, true);
    }

    throw error;
  }
};

const attemptSignIn = async (): Promise<string> => {
  const msal = await getMsalInstance();

  try {
    const cachedAccounts = msal.getAllAccounts();

    if (cachedAccounts.length === 1) {
      try {
        const silent = await msal.acquireTokenSilent({
          ...loginRequest,
          account: cachedAccounts[0]
        });
        if (silent.idToken) return silent.idToken;
      } catch (error) {
        // Silent acquisition failing is expected and recoverable - fall through
        // to the interactive popup below.
        if (!(error instanceof InteractionRequiredAuthError)) {
          console.warn('[MicrosoftAuth] Silent token acquisition failed:', error);
        }
      }
    }

    return await loginPopupWithRecovery(msal);
  } catch (error) {
    if (
      error instanceof BrowserAuthError &&
      (error.errorCode === 'user_cancelled' || error.errorCode === 'popup_window_error')
    ) {
      // Leaving the marker set here is what makes the next click fail, so drop
      // it on the way out.
      clearStaleInteraction();
      throw new MicrosoftSignInCancelled();
    }

    if (isInteractionInProgress(error)) {
      // The retry above did not help - do not show the user MSAL's aka.ms link.
      clearStaleInteraction();
      throw new Error(
        'A previous Microsoft sign-in was left open. Please try again.'
      );
    }

    if (error instanceof AuthError) {
      throw new Error(error.errorMessage || 'Microsoft sign-in failed.');
    }

    throw error;
  }
};

/**
 * Clear the cached Microsoft account.
 *
 * Local-only: it does not sign the user out of Microsoft across other tabs or
 * applications, which is the behaviour you want for an app-level logout.
 * Never throws - logout must not be blocked by MSAL state.
 */
export const clearMicrosoftSession = async (): Promise<void> => {
  if (!isMicrosoftAuthEnabled || !instance) return;

  try {
    const accounts = instance.getAllAccounts();
    for (const account of accounts) {
      await instance.clearCache({ account });
    }
  } catch (error) {
    console.warn('[MicrosoftAuth] Failed to clear cached account:', error);
  }
};
